import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { serverTimestamp } from "firebase/firestore";

import { login, logout, setDetails } from "../../redux/actions/authAction";
import { Firebase } from "../../containers/firebase";
import { COLLECTION } from "shared/strings/firebase";
import { getOrganizationById } from "services/organizations";
import { getCityById } from "services/cities";
import dateUtils from "utils/dateUtils";

export const USER_ID = 0;
export const USER_DATA = 1;

const AUTH_REGISTRATION_IN_PROGRESS_KEY = "auth_registration_in_progress";
export const AUTH_LOGIN_ERROR_KEY = "auth_login_error";

const getPreferredAccountType = ({ city, organisation, tenant, external }) => {
  const pathname = window.location?.pathname || "";

  if (pathname.startsWith("/organisation") && !organisation.empty) {
    return "organisation";
  }

  if (pathname.startsWith("/city") && !city.empty) {
    return "city";
  }

  if (pathname.startsWith("/tenant") && !tenant.empty && city.empty && organisation.empty) {
    return "tenant";
  }

  if (!city.empty) {
    return "city";
  }

  if (!organisation.empty) {
    return "organisation";
  }

  if (!tenant.empty) {
    return "tenant";
  }

  if (!external.empty) {
    return "external";
  }

  return null;
};

const getSnapshotData = (snapshot) => snapshot?.docs?.[0]?.data() || null;

const getFirstSnapshotDocId = (snapshot) => snapshot?.docs?.[0]?.id || null;

const toQueryLikeSnapshot = (docSnap) => {
  if (!docSnap?.exists) {
    return { empty: true, docs: [] };
  }

  return {
    empty: false,
    docs: [
      {
        id: docSnap.id,
        ref: docSnap.ref,
        data: () => docSnap.data(),
      },
    ],
  };
};

const resolveAccountSnapshot = async (firebase, collection, uid, querySnapshot) => {
  if (querySnapshot && !querySnapshot.empty) {
    return querySnapshot;
  }

  try {
    const byDocId = await firebase._findDocById(collection, uid);
    return toQueryLikeSnapshot(byDocId);
  } catch (error) {
    console.error(`Failed to resolve ${collection} by doc id`, error);
    return querySnapshot || { empty: true, docs: [] };
  }
};

const mergeExternalAccountDetails = ({ parentData = {}, externalData = {}, role = "" }) => {
  const safeParentData = parentData && typeof parentData === "object" ? parentData : {};
  const safeExternalData = externalData && typeof externalData === "object" ? externalData : {};

  const merged = {
    ...safeParentData,
    ...safeExternalData,
    role,
  };

  if (role === "organisation") {
    merged.cityId = safeExternalData?.cityId ?? safeParentData?.cityId ?? null;
    merged.organisationId = safeExternalData?.organisationId ?? safeParentData?.id ?? null;
  }

  if (role === "city") {
    merged.cityId = safeExternalData?.cityId ?? safeParentData?.id ?? null;
  }

  return merged;
};

export default function AuthProvider({ children }) {
  const dispatch = useDispatch();
  const userID = useSelector((state) => state.auth.user);

  const firebase = React.useMemo(() => new Firebase(), []);

  React.useEffect(() => {
    let unsubscribe;
    let userId;
    try {
      unsubscribe = firebase._onAuthStateChanged((user) => {
        const isAuthRegistrationInProgress =
          sessionStorage.getItem(AUTH_REGISTRATION_IN_PROGRESS_KEY) === "true";

        if (isAuthRegistrationInProgress) {
          return;
        }

        if (!user) {
          console.log("User not found");
          dispatch(logout());
        } else {
          userId = user.uid;
          Promise.all([
            firebase._findById(COLLECTION.Cities, user.uid),
            firebase._findById(COLLECTION.Organisations, user.uid),
            firebase._findById(COLLECTION.Tenants, user.uid),
            firebase._findDoc(COLLECTION.Tenants, ["userId", "==", user.uid]),
            firebase._findById(COLLECTION.externalUsers, user.uid),
          ])
            .then(async (snapshots) => {
              const [
                cityByIdField,
                organisationByIdField,
                tenantByIdField,
                tenantByUserId,
                externalByIdField,
              ] = snapshots;

              const [city, organisation, tenantById, external] = await Promise.all([
                resolveAccountSnapshot(firebase, COLLECTION.Cities, user.uid, cityByIdField),
                resolveAccountSnapshot(
                  firebase,
                  COLLECTION.Organisations,
                  user.uid,
                  organisationByIdField
                ),
                resolveAccountSnapshot(firebase, COLLECTION.Tenants, user.uid, tenantByIdField),
                resolveAccountSnapshot(
                  firebase,
                  COLLECTION.externalUsers,
                  user.uid,
                  externalByIdField
                ),
              ]);

              const tenant = tenantById?.empty ? tenantByUserId : tenantById;
              const preferredAccountType = getPreferredAccountType({
                city,
                organisation,
                tenant,
                external,
              });
              const findData = {
                city,
                organisation,
                tenant,
                external,
              }[preferredAccountType];

              if (!findData || findData.empty) {
                sessionStorage.setItem(AUTH_LOGIN_ERROR_KEY, "account_profile_not_found");
                await firebase._signOut();
                throw new Error("Account profile not found");
              }

              let role = preferredAccountType === "external" ? "" : preferredAccountType;
              let extraDataForExternal = {};
              let debounceTimeout;

              if (!organisation.empty) {
                await updateLoginHistory(organisation.docs[0].ref, debounceTimeout, firebase);
              }
              if (!tenant.empty) {
                await updateLoginHistory(tenant.docs[0].ref, debounceTimeout, firebase);
              }
              if (!external.empty) {
                const externalDoc = getSnapshotData(external);
                role = externalDoc.externalFor;

                const timePassMoreThenAvailable = dateUtils.hasPassed({
                  fromDate: externalDoc.lastSignIn?.toMillis() || 0,
                  differenceUnit: "hours",
                  differenceValue: 12,
                });

                if (timePassMoreThenAvailable) {
                  await external.docs[0].ref.update({
                    lastSignIn: firebase.firebase.firestore.Timestamp.now(),
                  });
                  await firebase._signOut();
                  return;
                }

                if (role === "organisation") {
                  const orgData = await getOrganizationById(externalDoc.organisationId);
                  extraDataForExternal = { ...orgData };
                }
                if (role === "city") {
                  const cityData = await getCityById(externalDoc.cityId);
                  extraDataForExternal = { ...cityData };
                }
                await updateLoginHistory(external.docs[0].ref, debounceTimeout, firebase);
                firebase._startTrackingOnlineStatus(user.uid);
              }

              const rawSnapshotData = getSnapshotData(findData);
              const data = !external.empty
                ? mergeExternalAccountDetails({
                    parentData: extraDataForExternal,
                    externalData: rawSnapshotData,
                    role,
                  })
                : {
                    ...extraDataForExternal,
                    ...rawSnapshotData,
                    role,
                  };

              if (role === "tenant") {
                data.tenantId =
                  rawSnapshotData?.tenantId ??
                  rawSnapshotData?.id ??
                  getFirstSnapshotDocId(tenant);
                data.userId = rawSnapshotData?.userId ?? user.uid;
              }

              let decodedStoredUser = null;
              try {
                if (typeof userID === "string" && userID.length > 0) {
                  const decodedUser = atob(userID);
                  decodedStoredUser = JSON.parse(decodedUser);
                }
              } catch (_) {
                decodedStoredUser = null;
              }

              sessionStorage.removeItem(AUTH_LOGIN_ERROR_KEY);
              dispatch(setDetails(data));

              if (user?.uid !== decodedStoredUser) {
                dispatch(login(user?.uid));
              }
            })
            .catch((error) => {
              console.error("Auth profile resolution failed", error);
              if (!sessionStorage.getItem(AUTH_LOGIN_ERROR_KEY)) {
                sessionStorage.setItem(AUTH_LOGIN_ERROR_KEY, "account_profile_not_found");
              }
              dispatch(logout());
            });
        }
      });
    } catch (error) {
      console.log("Auth error", error);
      dispatch(logout());
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
        firebase._stopTrackingOnlineStatus(userId);
      }
    };
  }, [firebase, dispatch, userID]);

  return children;
}

const updateLoginHistory = async (docRef, debounceTimeout, firebase) => {
  if (debounceTimeout) {
    clearTimeout(debounceTimeout);
  }

  debounceTimeout = setTimeout(async () => {
    const doc = await docRef.get();
    const data = doc.data();
    const loginHistory = data.loginHistory || {};
    const now = new Date();
    now.setHours(now.getHours());
    const today = now.toISOString().split("T")[0];
    loginHistory[today] = now;

    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    Object.keys(loginHistory).forEach((date) => {
      if (new Date(date) < sevenDaysAgo) {
        delete loginHistory[date];
      }
    });

    await docRef.set({ loginHistory, lastLogin: serverTimestamp() }, { merge: true });
  }, 5000);
};

export function useAuth() {
  const userFromStore = useSelector((state) => state.auth.user);
  const userDetailsFromStore = useSelector((state) => state.auth.data);
  const userAdminDataFromStore = useSelector((state) => state.auth.adminData);

  try {
    const decodeUser = userFromStore?.length > 0 ? atob(userFromStore) : null;
    const user = decodeUser ? JSON.parse(decodeUser) : null;
    const details = userDetailsFromStore;
    const adminData = userAdminDataFromStore;

    return [user, details, adminData];
  } catch (error) {
    return [null, null, null];
  }
}
